/**
 * Wall-clock date helpers for the agenda planner. Every function here
 * slices the `YYYY-MM-DD` string and constructs a UTC date so the
 * weekday/day labels stay consistent regardless of viewer timezone —
 * matching the wall-clock semantics used for `HH:MM` times elsewhere
 * in the feature.
 */

import { DEFAULT_DATE_LOCALE, type DateLocale } from "@/lib/date-format/date-format";

const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/** Decomposed view of a `YYYY-MM-DD` date string. */
export interface ParsedAgendaDate {
  /** Short weekday label, e.g. `"Mon"`. Empty when the input is malformed. */
  weekday: string;
  /** Day-of-month integer, e.g. `14`. Zero when the input is malformed. */
  day: number;
  /** Short "month day" string, e.g. `"May 14"`. Falls back to the raw input on parse failure. */
  monthDay: string;
}

/**
 * Parse a `YYYY-MM-DD` agenda date into the labels callers commonly need.
 * Returns zeros / empty strings for malformed input so consumers can
 * render defensively without throwing.
 *
 * @param date - Wall-clock date string.
 * @param locale - Date locale for `monthDay`; pass `useDateFormat().locale` on the dashboard.
 * @returns Decomposed labels — see {@link ParsedAgendaDate}.
 */
export function parseAgendaDate(date: string, locale: DateLocale = DEFAULT_DATE_LOCALE): ParsedAgendaDate {
  const [y, m, d] = date.split("-").map(Number);
  if (!y || !m || !d) return { weekday: "", day: 0, monthDay: date };
  const utc = new Date(Date.UTC(y, m - 1, d));
  return {
    weekday: WEEKDAYS_SHORT[utc.getUTCDay()],
    day: d,
    monthDay: utc.toLocaleDateString(locale, { month: "short", day: "numeric", timeZone: "UTC" }),
  };
}

/**
 * Today's wall-clock date in the viewer's local timezone as `YYYY-MM-DD`.
 * Drives the "today" pill on Day headers and the mobile tab strip's
 * initial selection. `toISOString().slice(0, 10)` would return UTC and
 * drift by one calendar day for users west of UTC in the late afternoon
 * / evening — we deliberately want the user's perception of "today".
 *
 * @returns `YYYY-MM-DD` in local time.
 */
export function getTodayLocal(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Render a full "Weekday, Month Day" label — used in the session-form
 * date dropdown where the longer form aids skimming.
 *
 * @param date - Wall-clock date string.
 * @param locale - Date locale; pass `useDateFormat().locale` on the dashboard.
 * @returns Long label like `"Mon, May 14"` (or `"Mon 14 May"`), or the raw input on parse failure.
 */
export function formatAgendaDateLong(date: string, locale: DateLocale = DEFAULT_DATE_LOCALE): string {
  const [y, m, d] = date.split("-").map(Number);
  if (!y || !m || !d) return date;
  const utc = new Date(Date.UTC(y, m - 1, d));
  return utc.toLocaleDateString(locale, {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}
